import ModalViewModel from 'infra/ModalViewModel'

import { AlertFactory, AlertState } from 'alerts/Alert'
import { BulkCopyJobs } from 'jobs/JobStore'

import { task } from 'mobx-task'
import { filter, Subscription } from 'rxjs'
import JobSearchAdapter from 'jobs/infra/JobSearchAdapter'
import SearchableJob from 'jobs/SearchableJob'
import { tapAction } from 'utils/mobx-util'
import { makeObservable } from 'mobx'
import BulkActionJobErrorViewModel from '../BulkActionJobErrorDialog/BulkActionJobErrorViewModel'
import { BulkActionJob } from 'jobs/bulk-actions'
import BulkCopyJobsSuccessViewModel from '../BulkCopyJobsSuccessDialog/BulkCopyJobsSuccessViewModel'

export default class BulkCopyJobsViewModel extends ModalViewModel {
  private jobs: BulkActionJob[] = []
  private clonedJobIds = new Set<string>()

  private subscription = new Subscription()

  constructor(
    private jobFetcher: JobSearchAdapter,
    private newAlert: AlertFactory,
    private bulkCopyJobsDelegate: BulkCopyJobs,
    private pushJob: (job: SearchableJob) => void,
    private copyErrorVM: BulkActionJobErrorViewModel,
    private bulkCopyJobsSuccessVM: BulkCopyJobsSuccessViewModel,
  ) {
    super()
    makeObservable(this)
  }

  get jobsCount() {
    return this.jobs.length
  }

  showConfirmation = async (jobs: BulkActionJob[]) => {
    this.jobs = jobs
    this.show()
  }

  copyJobs = task.resolved(async () => {
    const alert = this.newAlert()
      .show()
      .withState(AlertState.Pending)
      .saying('Copying jobs...')
    try {
      const response = await this.bulkCopyJobsDelegate(
        this.jobs.map((j) => j.id),
      )

      this.close()

      const errorList = response.getErrorsList().map((error) => ({
        jobId: error.getId(),
        error: error.getError(),
      }))

      if (errorList.length > 0) {
        this.copyErrorVM.showErrors(errorList, this.jobs)
      } else if (response.getIdsList().length > 0) {
        this.clonedJobIds = new Set(response.getIdsList())
        this.close()
        this.bulkCopyJobsSuccessVM.open(Array.from(this.clonedJobIds))
      }

      alert.dismiss()
    } catch (error) {
      alert.fromError(error)
      throw error
    }
  })

  activate() {
    this.subscription = this.jobFetcher.events$
      .pipe(
        filter(
          (e) => e.type === 'JobUpdated' && this.clonedJobIds.has(e.job.id),
        ),
        tapAction((e) => {
          this.pushJob(e.job)
          this.clonedJobIds.delete(e.job.id)
        }),
      )
      .subscribe()
  }

  deactivate() {
    this.subscription.unsubscribe()
  }
}
